import { Component, computed, inject, input, output } from '@angular/core';
import { IMDDropDownOption, MDDropDownComponent } from '@melodic-ui/components';
import { TimezoneStateService } from '@lb/state/timezone-state.service';

/**
 * `lb-timezone-dropdown`
 *
 * A shared, fully-featured timezone picker that wraps `md-drop-down` with:
 *   - Timezones sorted by current effective UTC offset, then alphabetically
 *   - Group header dividers per `timeZoneGroup` (e.g. "America", "Europe")
 *   - Labels in the format: "America/Detroit (UTC-4:00 · EDT)"
 *   - Hidden `searchTerms` (displayName + city + abbreviation) so users can
 *     search "Eastern Standard", "Detroit", "EDT" etc.
 *   - Built-in `[searchable]="true"` popup search input
 *   - Auto-scroll to the selected option when the popup opens (via MDDropDown)
 *
 * Usage:
 *   <lb-timezone-dropdown [value]="selectedID()" (change)="onTzChange($event)" />
 */
@Component({
	selector: 'lb-timezone-dropdown',
	standalone: true,
	imports: [MDDropDownComponent],
	template: `
		<md-drop-down
			[placeholder]="placeholder()"
			[searchable]="true"
			[options]="timezoneOptions()"
			[value]="value() ?? undefined"
			[disabled]="disabled()"
			(change)="change.emit($event)"
		></md-drop-down>
	`,
	styles: [
		`
			:host {
				display: flex;
				flex: 1;
			}
		`
	]
})
export class TimezoneDropdownComponent {
	private readonly _timezoneStateService = inject(TimezoneStateService);

	/** The currently selected timeZoneID (numeric). */
	value = input<number | null | undefined>(undefined);

	/** Disables the dropdown. */
	disabled = input<boolean | undefined>(undefined);

	/** Override the placeholder text shown when no value is selected. */
	placeholder = input<string>('Select a time zone');

	/** Emits the newly selected timeZoneID when the user makes a selection. */
	change = output<unknown>();

	timezoneOptions = computed<IMDDropDownOption[]>(() => {
		const timezones = this._timezoneStateService.timezones();
		if (timezones.length === 0) return [];

		const sorted = [...timezones].sort((a, b) => {
			const offsetA = a.isDST && a.utcOffsetDST !== null ? a.utcOffsetDST : a.utcOffset;
			const offsetB = b.isDST && b.utcOffsetDST !== null ? b.utcOffsetDST : b.utcOffset;
			if (offsetA !== offsetB) return offsetA - offsetB;
			return a.timeZoneName.localeCompare(b.timeZoneName);
		});

		const formatOffset = (offset: number) => {
			const sign = offset >= 0 ? '+' : '-';
			const abs = Math.abs(offset);
			const h = Math.floor(abs);
			const m = Math.round((abs - h) * 60);
			return `UTC${sign}${h}:${m.toString().padStart(2, '0')}`;
		};

		const options: IMDDropDownOption[] = [];
		let lastOffset: number | null = null;

		for (const tz of sorted) {
			const effectiveOffset = tz.isDST && tz.utcOffsetDST !== null ? tz.utcOffsetDST : tz.utcOffset;

			if (effectiveOffset !== lastOffset) {
				const region = tz.timeZoneGroup || tz.timeZoneName.split('/')[0] || '';
				const headerLabel = region ? `${region} · ${formatOffset(effectiveOffset)}` : formatOffset(effectiveOffset);
				options.push({ label: headerLabel, value: null, isDivider: true });
				lastOffset = effectiveOffset;
			}

			// Short city name: "America/New_York" → "New York"
			const shortName = tz.timeZoneName.includes('/') ? tz.timeZoneName.split('/').pop()!.replace(/_/g, ' ') : tz.timeZoneName;

			// Get live DST-aware abbreviation
			let abbreviation = tz.abbreviation || '';
			if (tz.timeZoneName) {
				try {
					const parts = new Intl.DateTimeFormat('en-US', {
						timeZone: tz.timeZoneName,
						timeZoneName: 'short'
					}).formatToParts(new Date());
					abbreviation = parts.find((p) => p.type === 'timeZoneName')?.value ?? abbreviation;
				} catch {
					// fall back to stored abbreviation
				}
			}

			// List label: "New York · EDT"
			// Selected pill: "America/New_York (UTC-4:00 · EDT)"
			// Search: city name, display name, city field, abbreviation (no continent prefix)
			const offsetStr = formatOffset(effectiveOffset);
			options.push({
				label: `${shortName} · ${abbreviation}`,
				selectedLabel: `${tz.timeZoneName} (${offsetStr} · ${abbreviation})`,
				value: tz.timeZoneID,
				searchTerms: [shortName, tz.displayName, tz.city, tz.abbreviation].filter(Boolean).join(' ')
			});
		}

		return options;
	});
}
